import {clsx} from 'clsx';
import {memo, useCallback, useMemo} from 'react';

import type {Team} from '#queries/teams/index.js';
import {useSwitchTeam, useTeams} from '#queries/teams/index.js';
import {useCurrentOrg, useCurrentTeam} from '#stores/session/index.js';
import {Avatar} from '#ui/atoms/avatar/index.js';
import {Flex} from '#ui/atoms/flex/index.js';
import {Text} from '#ui/atoms/text/index.js';
import {ComboBox} from '#ui/molecules/selects/combo_box/index.js';
import {applyPostTeamSwitchNavigation} from '#utils/post_team_switch_navigation.js';

import {useNavigationContext} from '../navigation/context.js';
import * as styles from './style.css.js';

const TeamOption = memo(function TeamOption({team}: {team: Team}) {
  return (
    <Flex direction="column" gap={1}>
      <Text className={styles.teamOptionName}>{team.name}</Text>
      {team.organization_name && (
        <Text as="div" className={styles.teamOptionOrganization}>
          {team.organization_name}
        </Text>
      )}
      {!!team.current_user_roles?.length && (
        <span className={styles.teamRoles}>
          {team.current_user_roles.map(role => role.label).join(', ')}
        </span>
      )}
    </Flex>
  );
});

type TeamAnchorProps = {
  selectedTeam: Team | null;
  organizationName?: string;
};

const TeamAnchor = memo(function TeamAnchor({
  selectedTeam,
  organizationName,
}: TeamAnchorProps) {
  const {collapsed} = useNavigationContext();
  if (!selectedTeam) {
    return <span>Select team</span>;
  }

  const orgName =
    selectedTeam.organization_name || organizationName || 'Unknown Org';

  return (
    <Flex direction="column" gap={1}>
      {collapsed ? (
        <Avatar name={selectedTeam.name} randomize />
      ) : (
        <>
          <div className={styles.teamNameLabel}>{selectedTeam.name}</div>
          <div className={styles.orgNameLabel}>{orgName}</div>
          {!!selectedTeam.current_user_roles?.length && (
            <div className={styles.teamRoles}>
              {selectedTeam.current_user_roles
                .map(role => role.label)
                .join(', ')}
            </div>
          )}
        </>
      )}
    </Flex>
  );
});

export const TeamSwitcher = memo(function TeamSwitcher() {
  const {collapsed} = useNavigationContext();
  const {data: apiTeams = []} = useTeams();
  const switchTeamMutation = useSwitchTeam();
  const currentTeam = useCurrentTeam();
  const currentOrg = useCurrentOrg();

  const itemToString = useCallback((team: Team | null) => {
    if (!team) return '';
    return `${team.name} ${team.organization_name} ${team.id} ${(team.current_user_roles ?? []).map(role => `${role.identifier} ${role.label}`).join(' ')}`;
  }, []);

  const handleTeamChange = useCallback(
    (selectedTeams: Team[]) => {
      const selectedTeam = selectedTeams[0];
      if (!selectedTeam) return;

      switchTeamMutation.mutate(
        {id: selectedTeam.id},
        {
          onSuccess: applyPostTeamSwitchNavigation,
        }
      );
    },
    [switchTeamMutation]
  );

  const initialSelectedTeam = useMemo(() => {
    if (!apiTeams.length) return undefined;

    if (!currentTeam?.id) return undefined;

    const found = apiTeams.find(team => team.id === currentTeam.id);
    return found ? [found] : undefined;
  }, [currentTeam, apiTeams]);

  if (apiTeams.length < 2) {
    return null;
  }

  return (
    <ComboBox<Team>
      key={currentTeam?.id ?? 'loading'}
      items={apiTeams}
      itemToString={itemToString}
      initialSelectedItems={initialSelectedTeam}
      onChange={handleTeamChange}
      isLoading={switchTeamMutation.isPending}
      clearable={false}
      allowCustomItems={false}
      asChild
    >
      <ComboBox.Anchor
        placeholder="Select team"
        isFullWidth={!collapsed}
        className={clsx(
          styles.anchorContainer,
          collapsed && styles.anchorContainerCollapsed
        )}
        asChild
      >
        {({selectedItems}) => (
          <Flex
            gap={2}
            align="start"
            justify={collapsed ? 'center' : 'between'}
            className={styles.anchorContent}
          >
            <TeamAnchor
              selectedTeam={
                apiTeams.find(
                  team =>
                    team.id ===
                    (selectedItems[0] as unknown as Team | undefined)?.id
                ) ?? null
              }
              organizationName={currentOrg?.name}
            />
            {collapsed ? null : <ComboBox.ToggleButton />}
          </Flex>
        )}
      </ComboBox.Anchor>
      <ComboBox.Surface>
        <ComboBox.Items<Team>>
          {({item}) => <TeamOption team={item} />}
        </ComboBox.Items>
      </ComboBox.Surface>
    </ComboBox>
  );
});
